import { Stack, Image, YStack, SizableText, Anchor, isWeb } from '@mythical/ui'
import { useStores } from 'app/store'
import { isIOS } from 'app/utils/constants'
import React from 'react'

import { UnlockButton } from '../content/UnlockButton'
import { ButtonLink } from '../primitive/Button'
import { IconLock } from '../primitive/Icon'
import LockModal from '../primitive/LockModal'
import { UpgradeButton } from './UpgradeButton'

type ContentLockModalProps = {
  onUnlock: () => void
  image?: string
  id: string
  isVideo?: boolean
  loading?: boolean
  planLevel: number | 'free'
  canUnlock: boolean
}

const ContentLockModal = ({
  id,
  image,
  onUnlock,
  isVideo,
  loading,
  planLevel,
  canUnlock,
}: ContentLockModalProps) => {
  const { userStore } = useStores()
  const loggedIn = !!userStore?.user?.id
  const loginLink = userStore?.generateLoginUrl()
  return (
    <YStack w="100%" position="relative" borderRadius="$2" zIndex="$1">
      {isVideo && (
        <Stack
          pos="absolute"
          overflow="hidden"
          borderRadius="$2"
          w="100%"
          bg="$secondary10"
          {...(isWeb ? { pb: '56.25%' } : { aspectRatio: 16 / 9 })}
        >
          {image && (
            <Image
              source={{ uri: image }}
              width="100%"
              height="auto"
              resizeMethod="scale"
              resizeMode="cover"
              flexGrow={1}
              pos={isWeb ? 'absolute' : 'relative'}
            />
          )}
        </Stack>
      )}
      <Stack
        borderRadius="$2"
        pos="absolute"
        w="100%"
        h="100%"
        zIndex={1}
        bg="$secondary10"
        opacity={isVideo ? 0.5 : 0}
      />
      <Stack
        $gtXs={{
          aspectRatio: 16 / 9,
        }}
        w="100%"
        zIndex="$2"
      >
        <LockModal flexGrow={1} id={id} onUnlock={onUnlock} loading={loading}>
          <YStack
            space="$4"
            jc="center"
            ai="center"
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            opacity={1}
          >
            <Stack p="$3" bg="$secondary8" borderRadius="$2">
              <IconLock size={24} color="$primary6" />
            </Stack>
            {!isIOS && (
              <SizableText
                fontFamily="$heading"
                textTransform="uppercase"
                size="$7"
                color="$primary1"
                textAlign="center"
                fontWeight="500"
              >
                Upgrade {canUnlock ? 'or unlock ' : ' '}to continue {isVideo ? 'watching' : 'reading'}
              </SizableText>
            )}
            {!isIOS && (
              <>
                <SizableText
                  textAlign="center"
                  size="$3"
                  fontWeight="600"
                  color="$secondary5"
                >
                  {isVideo
                    ? 'Upgrade to instantly unlock this video and other Mythical Society content by upgrading your subscription'
                    : 'Instantly unlock this and loads more Mythical content by upgrading your subscription'}
                </SizableText>
                {isVideo && canUnlock && (
                  <SizableText
                    textAlign="center"
                    size="$3"
                    fontWeight="600"
                    color="$secondary5"
                    pt="$3"
                  >
                    You can also choose to use your monthly unlock to watch this video.
                  </SizableText>
                )}
              </>
            )}
            {isIOS && (
              <SizableText
                fontFamily="$heading"
                textTransform="uppercase"
                size="$7"
                color="$primary1"
                textAlign="center"
                fontWeight="500"
              >
                You do not have the correct level of permissions to view this
                content
              </SizableText>
            )}

            {!isIOS && <UpgradeButton planLevel={planLevel} />}
            {canUnlock && <UnlockButton id={id} onUnlock={onUnlock} />}
            {!loggedIn && !isIOS && (
              <SizableText textAlign="center" size="$3" color="$secondary5">
                Already a member?{' '}
                <Anchor href={isWeb ? loginLink : '/login'} color="$primary10">
                  Sign in
                </Anchor>
              </SizableText>
            )}
            {isIOS && (
              <ButtonLink href={isWeb ? loginLink : '/login'} auth={!isWeb}>
                Sign in
              </ButtonLink>
            )}
          </YStack>
        </LockModal>
      </Stack>
    </YStack>
  )
}

export default ContentLockModal
